import React, { useState } from "react";
import { useUserApi } from "../../../../services/UserApi";
import Spinner from "carbon-react/lib/components/loader";
import MfaEnrollmentCard from "./MfaEnrollmentCard";
import RegenerateMfaRecoveryCode from "./RegenerateMfaRecoveryCode";
import InfoMessage from "../../../messages/InfoMessage";
import CommentsDialog from "../../../common/CommentsDialog";
import DeleteMfaEnrollmentDialog from "./deleteMfaEnrollmentDialog/DeleteMfaEnrollmentDialog";
import ErrorMessage from "../../../messages/ErrorMessage";
import { useAppContext } from "../../../../contexts/AppContext";
import DataLoadHeader from "../../../common/dataLoadHeader/DataLoadHeader";
import ResetMfaForUser from "./ResetMfaForUser";
import ResetMfaForUserDialog from "./resetMfaForUserDialog/ResetMfaForUserDialog";

const UserMfaEnrollments = ({ user }) => {
  const [errorMsg, setErrorMsg] = useState(null);
  const [showErrorMsg, setShowErrorMsg] = useState(false);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [commentsDialogState, setCommentsDialogState] = useState(null);
  const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [deleteMfaEnrollmentDialogOpen, setDeleteMfaEnrollmentDialogOpen] =
    useState(false);
  const { fetchMfaEnrollments } = useUserApi();
  const { agentEntitlement } = useAppContext();
  const [showNoEnrollmentsInfoBanner, setShowNoEnrollmentsInfoBanner] =
    useState(false);
  const [resetMfaDialogOpen, setResetMfaDialogOpen] = useState(false);

  const onLoadMfaEnrollments = async () => {
    await getEnrollments();
  };

  const clearError = () => {
    setErrorMsg(null);
    setShowErrorMsg(false);
  };

  const showError = (msg) => {
    setErrorMsg(msg);
    setShowErrorMsg(true);
  };

  const onError = (error) => {
    let msg = error?.toString();
    if (!msg || msg.length === 0) msg = "Operation failed";
    showError(msg);
  };

  const refreshEnrollments = async () => {
    await getEnrollments();
  };

  const getEnrollments = async () => {
    try {
      setShowNoEnrollmentsInfoBanner(false);
      setLoading(true);
      clearError();

      const result = await fetchMfaEnrollments(user?.userId);
      if (!result.success)
        throw new Error(
          result.error?.description || "Failed to fetch MFA enrollments"
        );
      setEnrollments(result.data);
      setShowNoEnrollmentsInfoBanner(result.data?.length === 0);
    } catch (error) {
      onError(error);
    }
    setLoading(false);
  };

  const openCommentsDialog = (dialogState) => {
    setCommentsDialogState(dialogState);
    setCommentsDialogOpen(true);
  };

  const onCommentsDialogClose = () => {
    setCommentsDialogState(null);
    setCommentsDialogOpen(false);
  };

  const openDeleteMfaEnrollmentDialog = (enrollment) => {
    setSelectedEnrollment(enrollment);
    setDeleteMfaEnrollmentDialogOpen(true);
  };

  const onDeleteMfaEnrollmentDialogClose = () => {
    setSelectedEnrollment(null);
    setDeleteMfaEnrollmentDialogOpen(false);
  };
  const openResetMfaDialog = () => {
    setResetMfaDialogOpen(true);
  };
  const closeResetMfaDialog = () => {
    setResetMfaDialogOpen(false);
  };
  const noEnrollmentsInfoBanner = (
    <>
      {showNoEnrollmentsInfoBanner ? (
        <InfoMessage message="User has no MFA Enrollments" />
      ) : null}
    </>
  );

  const regenerateMfaRecoveryCodeButton = (
    <>
      {!showNoEnrollmentsInfoBanner &&
        enrollments &&
        enrollments.length > 0 &&
        agentEntitlement &&
        agentEntitlement.users.canRegenerateMfaRecoveryCode && (
          <RegenerateMfaRecoveryCode
            user={user}
            openCommentsDialog={openCommentsDialog}
          />
        )}
    </>
  );

  const resetMfaForUserButton = (
    <>
      {!showNoEnrollmentsInfoBanner &&
        enrollments &&
        enrollments.length > 0 &&
        agentEntitlement &&
        agentEntitlement.users.canDeleteMfaEnrollments && (
          <ResetMfaForUser
            user={user}
            openResetMfaDialog={openResetMfaDialog}
          />
        )}
    </>
  );

  const mfaEnrollmentCards = (
    <>
      {!showNoEnrollmentsInfoBanner &&
      enrollments &&
      enrollments.length > 0 &&
      !loading ? (
        <div className="flex-wrap-card-container">
          {enrollments.map((enrollment) => {
            return (
              <MfaEnrollmentCard
                user={user}
                enrollment={enrollment}
                busy={loading}
                onError={onError}
                openDeleteMfaEnrollmentDialog={openDeleteMfaEnrollmentDialog}
              />
            );
          })}
        </div>
      ) : null}
    </>
  );

  const pageBody = (
    <>
      {noEnrollmentsInfoBanner}
      <div className="flex-wrap-card-container">
        {regenerateMfaRecoveryCodeButton}
        {resetMfaForUserButton}
      </div>
      {mfaEnrollmentCards}
    </>
  );

  const dataLoadHeader = (
    <DataLoadHeader onLoad={onLoadMfaEnrollments} appearBusy={loading} />
  );

  if (user === null) return null;

  return (
    <>
      {dataLoadHeader}
      {showErrorMsg ? (
        <ErrorMessage message={errorMsg || "The operation failed"} />
      ) : (
        <>{pageBody}</>
      )}
      <CommentsDialog
        dialogState={commentsDialogState}
        open={commentsDialogOpen}
        onClose={onCommentsDialogClose}
      />
      <DeleteMfaEnrollmentDialog
        open={deleteMfaEnrollmentDialogOpen}
        onClose={onDeleteMfaEnrollmentDialogClose}
        user={user}
        enrollment={selectedEnrollment}
        onEnrollmentDeleted={refreshEnrollments}
      />
      <ResetMfaForUserDialog
        open={resetMfaDialogOpen}
        onClose={closeResetMfaDialog}
        user={user}
        onResetMFA={refreshEnrollments}
      />
    </>
  );
};

export default UserMfaEnrollments;
